/* eslint-disable max-statements */
/* eslint-disable no-mixed-spaces-and-tabs */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import React from 'react';
import { BsCheck2Circle } from 'react-icons/bs';
import { useInfiniteQuery, useMutation } from 'react-query';
import axios from 'axios';
import LoadingSixImagesAnim from '../../Utils/LoadingAnimations/LoadingImages';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { useDispatch } from 'react-redux';
import { SetNotificationAction } from '../../../redux/actions/notificationAction';
import { stateNotificationsEnum } from '../../Utils/notification';
import { globalEnv } from '../../../config/baseConfig';
import LazyImage from '../../Tables/TablesList/Components/imageComponent';
import { hideSearchbarAction } from '../../../redux/actions/searchBarAction';
import placeholder from '../../../assets/images/placeholder.png';

type props = {
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	selectedTopics: any[];
	setTopics: (topics: any[]) => void;
	userId: string;
};

export default function TopicAddModal({
	isOpen = false,
	setOpen,
	selectedTopics,
	setTopics,
	userId
}: props) {
	const cancelButtonRef = useRef(null);

	const [isPaused, setPause] = useState(false);
	const [addedTopics, setAddedTopics] = useState<any[]>([]);
	const [removedTopics, setRemovedTopics] = useState<any[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery } = useTypedSelector((state) => state.searchbar);

	const dispatch = useDispatch();

	const fetchTopics = async ({ pageParam = 1 }) => {
		console.log(pageParam);
		const response = await fetch(
			`${globalEnv.BASE_URL}/topic/get-all?page=${pageParam}&isLongList=true${
				searchQuery !== '' ? '&filter=' + searchQuery : ''
			}`
		);
		const results = await response.json();
		return {
			results,
			nextPage: pageParam + 1,
			totalPages: results.meta.pages
		};
	};

	const { data, isLoading, isFetching, isError, fetchNextPage } =
		useInfiniteQuery(['topics', searchQuery], fetchTopics, {
			getNextPageParam: (lastPage) => {
				if (lastPage.nextPage <= lastPage.totalPages) return lastPage.nextPage;
				return undefined;
			}
		});

	interface iSubscribeToTopicsReq {
		userId: string;
	}

	const subscribeToTopicsReq = async (data: iSubscribeToTopicsReq) => {
		const dto = {
			userId: data.userId,
			addedTopics: addedTopics.map((topic) => topic._id),
			removedTopics: removedTopics.map((topic) => topic._id)
		};
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + '/admin/user/sub-unsub-topics',
			dto,
			config
		);
		return response;
	};

	const subscribeToTopics = useMutation(subscribeToTopicsReq, {
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					message: 'Successfully subscribed to topics!',
					type: stateNotificationsEnum.SUCCESS
				})
			);
			setOpen(false);
		},
		onError: () => {
			dispatch(
				SetNotificationAction({
					message: 'An error occured!',
					type: stateNotificationsEnum.ERROR
				})
			);
			setIsSaving(false);
		}
		// onSettled: () => {
		// 	queryClient.invalidateQueries('create');
		// }
	});

	const listInnerRef = useRef<null>(null);

	const onScroll = () => {
		if (!isFetching) {
			if (listInnerRef.current) {
				const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
				if (scrollTop + clientHeight >= scrollHeight - 600) {
					if (!isPaused) {
						fetchNextPage();
						setPause(true);
					}
				} else undoPause();
			}
		}
	};

	const undoPause = () => {
		setTimeout(() => {
			setPause(false);
		}, 1000);
	};

	const checkIfTopicIsSelected = (id: string) => {
		if (selectedTopics.some((e) => e._id === id)) return true;
		return addedTopics.some((e: any) => e._id === id) ? true : false;
	};

	const save = () => {
		let temp = selectedTopics;
		if (removedTopics !== null)
			removedTopics.forEach((topic: any) => {
				temp = temp.filter((item) => item._id !== topic._id);
			});

		if (addedTopics !== null) temp = temp.concat(addedTopics);
		if (temp.length > 0) {
			setIsSaving(true);
			subscribeToTopics.mutate({ userId });
			setTopics(temp);
		} else {
			dispatch(
				SetNotificationAction({
					message: 'An error occured!',
					type: stateNotificationsEnum.ERROR
				})
			);
		}
	};

	useEffect(() => {
		return () => {
			console.log('Unmount!');
		};
	}, []);

	return (
		<div>
			<Transition.Root
				show={isOpen}
				as={Fragment}
				enter="transition duration-100 ease-out"
				enterFrom="transform scale-95 opacity-0"
				enterTo="transform scale-100 opacity-100"
				leave="transition duration-75 ease-out"
				leaveFrom="transform scale-100 opacity-100"
				leaveTo="transform scale-95 opacity-0"
			>
				<Dialog
					as="div"
					className="fixed z-10 inset-0 pt-5"
					initialFocus={cancelButtonRef}
					onClose={() => {
						dispatch(hideSearchbarAction());
						setOpen(false);
					}}
				>
					<div className="flex items-end justify-center min-h-screen text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
						>
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div
								className="relative inline-block bg-white dark:bg-gray-800 rounded-lg text-left
                                       shadow-xl transform transition-all align-middle ml-20 mr-5
                                       max-w-7xl h-[85vh] overflow-hidden lg:w-10/12 w-9/12 my-10 sm:my-0"
							>
								<section
									className="text-gray-700 overflow-scroll h-[75vh]"
									onScroll={() => onScroll()}
									ref={listInnerRef}
								>
									<div className="container sm:px-2 md:px-20 mx-auto lg:pt-5 text-center">
										<h3 className="lg:text-5xl text-4xl mt-3 lg:mt-0 font-semibold text-gray-800 dark:text-white">
											Subscribe to Topics
										</h3>
										<div className="border-t mt-3 mb-5 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center"></div>

										<div className="flex flex-wrap -m-1 md:-m-2 w-full">
											{isLoading ? (
												<div className="flex flex-wrap w-full justify-center mt-3">
													<LoadingSixImagesAnim />
												</div>
											) : isError ? (
												<p>There was an error</p>
											) : (
												data?.pages.map((page) =>
													page.results.topics.map((item: any) => (
														<SingleTopic
															key={item._id}
															item={item}
															selectedTopics={selectedTopics}
															isSelected={checkIfTopicIsSelected(item._id)}
															addedTopics={addedTopics}
															setAddedTopics={setAddedTopics}
															removedTopics={removedTopics}
															setRemovedTopics={setRemovedTopics}
														/>
													))
												)
											)}
											{isSaving ||
												(isFetching && (
													<div className="flex flex-wrap w-full justify-center mt-3">
														<LoadingSixImagesAnim />
													</div>
												))}
										</div>
									</div>
								</section>
								<div className="bg-white dark:bg-gray-800 px-4 pb-3 sm:px-6 sm:flex sm:flex-row-reverse">
									{(removedTopics.length > 0 || addedTopics.length > 0) && (
										<button
											disabled={isSaving ? true : false}
											type="button"
											className="btn btn-outline btn-sm btn-primary mt-2 ml-1"
											onClick={() => save()}
										>
											save
											{isSaving ? (
												<div className="pl-2">
													<svg
														role="status"
														className="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
														viewBox="0 0 100 101"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
															fill="currentColor"
														></path>
														<path
															d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
															fill="currentFill"
														></path>
													</svg>
												</div>
											) : (
												<h5 className="text-xs ml-2">💾</h5>
											)}
										</button>
									)}
									<button
										type="button"
										className="btn btn-outline btn-sm btn-error mt-2 mr-2"
										onClick={() => setOpen(false)}
										ref={cancelButtonRef}
									>
										Cancel
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</div>
	);
}

type singleTopicProps = {
	item: any;
	isSelected: boolean;
	selectedTopics: any[];

	addedTopics: any[];
	setAddedTopics: (item: any) => void;

	removedTopics: any[];
	setRemovedTopics: (item: any) => void;
};

const SingleTopic = (props: singleTopicProps) => {
	const [isSelected, setSelected] = useState(props.isSelected);
	const [random, setRandom] = useState(Math.floor(Math.random() * 101));

	const updateSelect = () => {
		if (!isSelected) props.setAddedTopics([...props.addedTopics, props.item]);
		else {
			if (props.selectedTopics.some((e) => e._id === props.item._id))
				props.setRemovedTopics([...props.removedTopics, props.item]);
			else
				props.setAddedTopics(
					props.addedTopics.filter((item) => item._id !== props.item._id)
				);
		}
		setSelected(!isSelected);
	};

	console.log(props.item.backgroundImage.url_low_res);

	return (
		<div
			className="flex flex-wrap flex-col w-1/3 p-4"
			onClick={() => updateSelect()}
		>
			<div className="relative rounded-lg flex justify-center content-center w-full aspect-[16/10] dark:bg-gray-600 bg-gray-300 mb-2">
				<LazyImage
					photo={
						props.item.backgroundImage.url_low_res != undefined
							? props.item.backgroundImage.url_low_res
							: placeholder
					}
					style={
						'block object-cover object-center w-full aspect-[16/10] rounded-lg'
					}
					loaderDimesnions={80}
					effect={'blur'}
				/>
				<div
					className={
						isSelected
							? 'absolute inset-0 bg-gradient-to-t rounded-lg from-black/75 opacity-100 text-center transition-colors duration-500'
							: 'absolute inset-0 bg-gradient-to-t rounded-lg from-black/75 opacity-0 hover:opacity-100 transition-all duration-500 text-center'
					}
				></div>
				{isSelected ? (
					<BsCheck2Circle
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 dark:text-primary text-center transition-all duration-500 opacity-100"
					/>
				) : (
					<BsCheck2Circle
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 mx-2 dark:text-primary text-center transition-all duration-500 opacity-0 scale-0"
					/>
				)}
			</div>

			<h3 className="font-medium dark:text-white">{props.item.name}</h3>
		</div>
	);
};
